import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { UserLanguageSync } from '../auth/UserLanguageSync';

import { useLanguage } from '../../hooks/useLanguage';
import { isAuthenticated } from '../../utils/tokenManager';

import { SoftField } from '../landing/SoftField';
import { Footer } from './Footer';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { isRTL } = useLanguage();
  const location = useLocation();
  const auth = isAuthenticated();

  useEffect(() => {
    if (!isSidebarOpen && !isDesktop) {
      menuButtonRef.current?.focus({ preventScroll: true });
    }
  }, [isSidebarOpen, isDesktop]);

  useEffect(() => {
    const checkScreenSize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      // Desktop: always keep side nav open. Mobile: closed by default.
      setIsSidebarOpen(desktop);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isDesktop]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (!auth || isDesktop) return;
    document.body.style.overflow = isSidebarOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [auth, isDesktop, isSidebarOpen]);

  const handleMenuToggle = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className={`min-h-screen flex flex-col bg-brand-primary overflow-x-hidden ${auth ? 'app-shell' : 'marketing-shell'}`}>
      <SoftField />
      {auth ? <UserLanguageSync /> : null}
      <Header
        onMenuToggle={auth ? handleMenuToggle : undefined}
        isMenuOpen={isSidebarOpen}
        menuButtonRef={auth ? menuButtonRef : undefined}
      />

      <div className={`flex flex-1 relative min-w-0 overflow-x-hidden ${auth ? 'pt-[56px] sm:pt-[60px]' : 'pt-[56px] sm:pt-20'}`}>
        {auth && <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />}

        <main
          className={`flex-1 transition-all duration-300 min-w-0 overflow-x-hidden ${
            auth ? (isRTL ? 'lg:mr-[288px]' : 'lg:ml-[288px]') : ''
          }`}
          role="main"
        >
          <div
            className={
              auth
                ? 'dx-app-content relative z-[1] mx-auto max-w-7xl px-3 py-4 sm:px-4 md:py-6'
                : 'dx-marketing-wrap relative z-[1] w-full min-w-0 max-w-full'
            }
          >
            {children}
          </div>
        </main>
      </div>

      {!auth && <Footer />}
    </div>
  );
}
